import { ArrowUpRight, MessageSquareMoreIcon } from "lucide-react";

import { IgAiIcon, IgButton, IgTypography } from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";

import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { useDispatch, useSelector } from "@src/store";
import {
  VivIdeasAction,
  VivIdeasSelector,
} from "@src/store/slices/experienceBuilder/ideas";

/**
 * Chat empty state for Viv: the featured Idea Library prompts as clickable
 * chips, plus a link into the full library.
 *
 * Mounted via the panel's `conversationEmpty` slot (see VivChat), so it renders
 * only while the conversation has no turns.
 */
export const VivEmptyState = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const featured = useSelector(VivIdeasSelector.getFeaturedIdeas);
  const { disabled } = useExecutionPanelContext();

  return (
    <Stack
      spacing={4}
      sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}
    >
      <Stack spacing={3} sx={{ alignItems: "center" }}>
        <IgAiIcon icon={MessageSquareMoreIcon} surface="round" shine />
        <Stack spacing={1} sx={{ alignItems: "center" }}>
          <IgTypography variant="h2" fontWeight={600}>
            Ask AI to make changes
          </IgTypography>
          <IgTypography variant="body2" color={theme.palette.text.secondary}>
            Click one of the prompts to send
          </IgTypography>
        </Stack>
      </Stack>

      <Stack spacing={3} sx={{ width: "100%", alignItems: "center" }}>
        {featured.map((idea) => (
          <IgButton
            key={idea.id}
            color="secondary"
            disabled={disabled}
            onClick={() =>
              void dispatch(
                VivIdeasAction.sendIdea({ idea, source: "featured" })
              )
            }
            sx={{
              maxWidth: "100%",
              justifyContent: "flex-start",
              textAlign: "left",
              border: `1px solid ${theme.igBorders.core.default}`,
            }}
          >
            <IgTypography variant="body2" color={theme.palette.text.primary}>
              {idea.title}
            </IgTypography>
          </IgButton>
        ))}
      </Stack>

      <IgButton
        color="link"
        onClick={() => void dispatch(VivIdeasAction.openLibrary())}
        // A global `.lucide { color: #4a4a4a }` (App.css) forces every lucide
        // icon gray; override it here so the arrow inherits the link's accent
        // `currentColor` and follows the hover shade.
        sx={{ gap: 1, "& .lucide": { color: "inherit" } }}
      >
        Browse more ideas
        <ArrowUpRight size={16} />
      </IgButton>
    </Stack>
  );
};
